import {
    AutocompleteInput,
    DateInput,
    Edit,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    TextInput,
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ClipboardList, DollarSign, User, ArrowLeft } from "lucide-react";
import { useDataProvider, useNotify, useRedirect } from "ra-core";
import { useWatch } from "react-hook-form";
import { getNextRef } from "../documents/shared/documentReference";
import { Button } from "@/components/ui/button";
import { Link } from "react-router";
import { FileInput } from "@/components/admin/file-input";
import { FilteredContactInput } from "../misc/FilteredContactInput";
import { AttachmentField } from "../notes/AttachmentField";
import { ProductRequirements } from "./ProductRequirements";

const LostReasonInput = () => {
    const status = useWatch({ name: 'status' });
    if (status !== 'disqualified') return null;

    return (
        <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200">
            <TextInput
                source="lost_reason"
                label="Reason for Disqualification"
                multiline
                rows={2}
                className="w-full"
            />
        </div>
    );
};

export const EnquiryEdit = () => {
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();

    const onSuccess = async (data: any, variables: any, _context: any) => {
        notify('Enquiry updated successfully', { type: 'success' });
        
        // Auto-create Opportunity when moving to 'in_discussion'
        if (data.status === 'in_discussion' && variables.previousData?.status !== 'in_discussion') {
            try {
                // Check if opportunity already exists
                const { data: existingOpps } = await dataProvider.getList('opportunities', {
                    pagination: { page: 1, perPage: 1 },
                    sort: { field: 'id', order: 'ASC' },
                    filter: { enquiry_id: data.id }
                });

                if (existingOpps.length === 0) {
                    const { data: allOpps } = await dataProvider.getList('opportunities', {
                        pagination: { page: 1, perPage: 1000 },
                        sort: { field: 'id', order: 'DESC' },
                        filter: {}
                    });
                    const nextRef = getNextRef('opportunity', allOpps.map((o: any) => o.reference).filter(Boolean));

                    await dataProvider.create('opportunities', {
                        data: {
                            reference: nextRef,
                            enquiry_id: data.id,
                            company_id: data.company_id,
                            contact_id: data.contact_id,
                            stage: 'requirement_discussion',
                            status: 'open',
                            deal_value: data.requirements?.estimated_budget || data.requirements?.budget_min || 0,
                        }
                    });
                    notify('Opportunity created automatically', { type: 'info' });
                }
            } catch (error: any) {
                console.error(error);
                notify(`Error creating opportunity: ${error.message}`, { type: 'warning' });
            }
        }
        
        redirect('list', 'enquiries');
    };

    return (
        <Edit title="Edit Enquiry" mutationMode="pessimistic" mutationOptions={{ onSuccess }}>
            <SimpleForm className="max-w-5xl mx-auto space-y-6">
                {/* Top Left Back Button */}
                <div className="flex justify-start w-full">
                    <Button asChild variant="outline" size="sm" className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                        <Link to="/enquiries" className="flex items-center gap-1.5">
                            <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back to Enquiries
                        </Link>
                    </Button>
                </div>

                {/* Header Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-slate-50/50">
                        <div className="flex items-center gap-2">
                            <ClipboardList className="w-5 h-5 text-slate-500" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Enquiry Details</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <TextInput source="reference" label="Enquiry No." disabled className="w-full font-mono opacity-70" helperText="Auto-generated" />
                            <SelectInput
                                source="source"
                                label="Lead Source"
                                choices={[
                                    { id: 'Website', name: 'Website' },
                                    { id: 'Email', name: 'Email' },
                                    { id: 'Phone', name: 'Phone / WhatsApp' },
                                    { id: 'Reference', name: 'Reference' },
                                    { id: 'Cold Call', name: 'Cold Calling' },
                                ]}
                                className="w-full"
                            />
                            <SelectInput
                                source="status"
                                label="Status"
                                choices={[
                                    { id: 'new', name: 'New' },
                                    { id: 'in_discussion', name: 'In Discussion' },
                                    { id: 'qualified', name: 'Qualified' },
                                    { id: 'disqualified', name: 'Disqualified' },
                                    { id: 'converted', name: 'Converted' },
                                    { id: 'closed', name: 'Closed' },
                                ]}
                                className="w-full"
                            />
                        </div>
                        <LostReasonInput />
                    </CardContent>
                </Card>

                {/* Customer Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-slate-50/50">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5 text-slate-500" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Customer Information</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <ReferenceInput source="assigned_to" reference="sales">
                                <AutocompleteInput label="Assigned Sales Rep" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                            {/* V-8: Initial/Qualification Score + checklist removed per client request */}
                        </div>
                        <div className="pt-4">
                            <FileInput
                                source="attachments"
                                label="Attachments (drawings, PDF, images)"
                                multiple
                                accept={{
                                    "application/pdf": [".pdf"],
                                    "image/*": [".png", ".jpg", ".jpeg"],
                                    "application/msword": [".doc"],
                                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                                    "application/vnd.ms-excel": [".xls"],
                                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                                }}
                            >
                                <AttachmentField source="src" title="title" target="_blank" />
                            </FileInput>
                        </div>
                    </CardContent>
                </Card>

                {/* Requirements Card (E-3 / V-9: Budget removed per client request) */}
                <Card className="w-full shadow-sm border-t-4 border-t-slate-900">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-slate-500" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Requirements</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <DateInput source="requirements.timeline" label="Expected Timeline" className="w-full" />
                            <ProductRequirements />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <ReferenceInput source="company_id" reference="companies">
                                <AutocompleteInput label="Company" className="w-full" />
                            </ReferenceInput>
                            <FilteredContactInput label="Contact Person" className="w-full" />
                            <ReferenceInput source="ops_head_id" reference="sales">
                                <AutocompleteInput label="Assigned Ops Head" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                            <ReferenceInput source="ops_team_member_id" reference="sales">
                                <AutocompleteInput label="Assigned Ops Team Member" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                        </div>
                    </CardContent>
                </Card>
            </SimpleForm>
        </Edit>
    );
};
